import { ChangeEventHandler, MouseEventHandler, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../../../../../app/Context';
import { i18nNS } from '../../../../../i18n';
import { useTimerContext } from '../Context';
import useImageUpload from './hooks';
import { getTimerMetaData, setTimerMetaData } from '../../../../../db/timer';

export const ALLOWED_FILE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'webp'];

const useBackground = () => {
  const { t } = useTranslation([i18nNS.TIMER, i18nNS.COMMON]);
  const { presentationId, activeSlideId, isPresenting, setSlideTitleAndSubtitle, setError } = useAppContext();

  const { backgroundImage, setBackgroundImage } = useTimerContext();

  const { handleImageUpload, handleCancelImageUpload } = useImageUpload();

  const slideTitle = t('acadly_activity_plugin', {
    ns: i18nNS.COMMON,
  });

  const slideSubTitle = useMemo(() => {
    if (!presentationId) return '';
    if (!activeSlideId) return '';

    const response = getTimerMetaData(presentationId, activeSlideId);

    if (!response) return '';
    const { countdownFrom = 0, title } = response;

    if (countdownFrom <= 0) {
      return t('timer_was_not_started', { ns: i18nNS.TIMER });
    }

    const minutes = `${Math.floor(countdownFrom / 60)}`.padStart(2, '0');
    const seconds = `${countdownFrom % 60}`.padStart(2, '0');

    return t('title_timer_ran_for_minutes_count_minutes_seconds_count_seconds', {
      ns: i18nNS.TIMER,
      title: title ?? t('untitled_timer', { ns: i18nNS.TIMER }),
      minutesCount: minutes,
      secondsCount: seconds,
    });
  }, []);

  const handleFileSelect: ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!presentationId) return;
    if (!activeSlideId) return;

    const { files } = event.target;
    if (!files) return;

    const file = files?.[0];
    if (!file) return;

    const isFileAllowed = ALLOWED_FILE_EXTENSIONS.some((extension) => file.type.includes(extension));
    if (!isFileAllowed) {
      setError(
        t('uploaded_file_is_not_supported_supported_image_extensions_are_allowed_extensions', {
          ns: i18nNS.TIMER,
          allowedExtensions: ALLOWED_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(','),
        })
      );
      return;
    }

    // file size limit = 3MB
    if (file.size / 1024 / 1024 > 3) {
      setError(
        t('uploaded_image_is_larger_than_maximum_allowed_size_of_N_mb', { ns: i18nNS.TIMER, count: 3 })
      );
      return;
    }

    const fileReader = new FileReader();

    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      const dataUrl = fileReader.result as string;
      setBackgroundImage(dataUrl);
    };

    // Abort previous request if it exists
    handleCancelImageUpload();

    const response = await handleImageUpload(file);

    if (!response) return;
    const { downloadUrl } = response;

    setBackgroundImage(downloadUrl);
    setTimerMetaData(presentationId, activeSlideId, { bgImg: downloadUrl });

    setSlideTitleAndSubtitle({ title: slideTitle, subtitle: slideSubTitle });
  };

  const handleFileInputClick: MouseEventHandler<HTMLInputElement> = (e) => {
    (e.target as any).value = '';
  };

  const handleRemoveFile = () => {
    if (!presentationId) return;
    if (!activeSlideId) return;

    // Abort previous request if it exists
    handleCancelImageUpload();

    setTimerMetaData(presentationId, activeSlideId, { bgImg: '' });
    setBackgroundImage('');
    setSlideTitleAndSubtitle({ title: slideTitle, subtitle: slideSubTitle });
  };

  useEffect(() => {
    if (!presentationId) return;
    if (!activeSlideId) return;
    const response = getTimerMetaData(presentationId, activeSlideId);
    if (!response) return;
    const { bgImg = '' } = response;
    setBackgroundImage(bgImg);
  }, [presentationId, activeSlideId]);

  return { handleFileSelect, handleFileInputClick, handleRemoveFile, backgroundImage, isPresenting };
};

export default useBackground;
